import { supabase } from '../lib/supabase';
import { subscribeToRestaurantChanges } from '../utils/realtimeSubscription';

const requireRestaurantId = (restaurantId) => {
  if (!restaurantId) {
    throw new Error('Restaurant context is required');
  }
};

/**
 * Increment AR use count for a dish
 */
export const trackARUse = async (dishId, dishName, restaurantId) => {
  if (!restaurantId) return;
  try {
    const { error } = await supabase?.rpc('increment_ar_use', {
      p_dish_id: String(dishId),
      p_dish_name: dishName || 'Unknown Dish',
      p_restaurant_id: restaurantId || null,
    });
    if (error) console.warn('AR tracking error:', error?.message);
  } catch (err) {
    console.warn('AR tracking failed:', err?.message);
  }
};

/**
 * Increment QR scan count for a dish
 */
export const trackQRScan = async (dishId, dishName, restaurantId) => {
  if (!restaurantId) return;
  try {
    const { error } = await supabase?.rpc('increment_qr_scan', {
      p_dish_id: String(dishId),
      p_dish_name: dishName || 'Unknown Dish',
      p_restaurant_id: restaurantId || null,
    });
    if (error) console.warn('QR scan tracking error:', error?.message);
  } catch (err) {
    console.warn('QR scan tracking failed:', err?.message);
  }
};

/**
 * Fetch all dish stats ordered by AR uses descending
 */
export const fetchDishStats = async (restaurantId) => {
  requireRestaurantId(restaurantId);
  const { data, error } = await supabase?.from('dish_stats')?.select('*')?.eq('restaurant_id', restaurantId)?.order('ar_uses', { ascending: false });
  if (error) throw error;
  return data || [];
};

/**
 * Fetch dish stats for the CURRENT month only (starts from zero each month).
 * Dishes with no activity this month are listed with 0 views.
 */
export const fetchCurrentMonthDishStats = async (restaurantId) => {
  requireRestaurantId(restaurantId);
  const now = new Date();
  const monthKey = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}-01`;

  const [allRes, monthRes] = await Promise.all([
    supabase?.from('dish_stats')?.select('id, dish_id, dish_name')?.eq('restaurant_id', restaurantId),
    supabase?.from('dish_stats_monthly')?.select('dish_id, dish_name, ar_uses, qr_scans')?.eq('restaurant_id', restaurantId)?.eq('month', monthKey),
  ]);
  if (allRes?.error) throw allRes?.error;
  if (monthRes?.error) throw monthRes?.error;

  const monthMap = new Map((monthRes?.data || [])?.map((r) => [r?.dish_id, r]));
  const rows = (allRes?.data || [])?.map((d) => ({
    id: d?.id,
    dish_id: d?.dish_id,
    dish_name: d?.dish_name,
    ar_uses: monthMap?.get(d?.dish_id)?.ar_uses || 0,
    qr_scans: monthMap?.get(d?.dish_id)?.qr_scans || 0,
  }));

  return rows?.sort((a, b) => b?.ar_uses - a?.ar_uses);
};

/**
 * Fetch aggregate totals: total AR uses, total QR scans
 */
export const fetchTotals = async (restaurantId) => {
  requireRestaurantId(restaurantId);
  const { data, error } = await supabase?.from('dish_stats')?.select('ar_uses, qr_scans')?.eq('restaurant_id', restaurantId);
  if (error) throw error;
  const totalAR = (data || [])?.reduce((sum, row) => sum + (row?.ar_uses || 0), 0);
  const totalQR = (data || [])?.reduce((sum, row) => sum + (row?.qr_scans || 0), 0);
  return { totalAR, totalQR };
};

// Storage keys — scoped per restaurant to avoid cross-restaurant contamination
const getBaselineKey = (restaurantId) => `dish_stats_month_baseline_v3_${restaurantId || 'default'}`;
const getPrevMonthKey = (restaurantId) => `dish_stats_prev_month_totals_v3_${restaurantId || 'default'}`;

const getMonthKey = (date) => {
  const d = date || new Date();
  return `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, '0')}`;
};

const readJSON = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return null;
};

const writeJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {}
};

/**
 * Calculate percentage change from base to current.
 * Returns { value: string, type: 'positive'|'negative'|'neutral' } or null
 */
const calcPercent = (current, base) => {
  if (base === null || base === undefined) return null;
  if (base === 0) {
    if (current === 0) return { value: '0%', type: 'neutral' };
    return { value: '+100%', type: 'positive' };
  }
  const diff = current - base;
  const pct = Math.round((diff / base) * 100);
  if (pct === 0) return { value: '0%', type: 'neutral' };
  return {
    value: pct > 0 ? `+${pct}%` : `${pct}%`,
    type: pct > 0 ? 'positive' : 'negative',
  };
};

/**
 * Returns MONTHLY totals (resets to 0 each month) plus % change vs last month.
 * Scoped to a specific restaurant when restaurantId is provided.
 */
export const fetchTotalsWithComparison = async (restaurantId) => {
  requireRestaurantId(restaurantId);
  const { data, error } = await supabase?.from('dish_stats')?.select('ar_uses, qr_scans, dish_id')?.eq('restaurant_id', restaurantId);
  if (error) throw error;

  const rows = data || [];
  const cumulativeAR = rows?.reduce((sum, row) => sum + (row?.ar_uses || 0), 0);
  const restaurantRow = rows?.find(row => row?.dish_id === 'restaurant');
  const cumulativeQR = restaurantRow?.qr_scans || 0;
  const cumulativePopular = rows?.filter(d => (d?.ar_uses || 0) > 0)?.length;

  const currentMonthKey = getMonthKey();
  const BASELINE_KEY = getBaselineKey(restaurantId);
  const PREV_MONTH_KEY = getPrevMonthKey(restaurantId);
  const baseline = readJSON(BASELINE_KEY);
  const prevMonth = readJSON(PREV_MONTH_KEY);

  if (!baseline) {
    writeJSON(BASELINE_KEY, {
      month: currentMonthKey,
      ar: cumulativeAR,
      qr: cumulativeQR,
      popular: cumulativePopular,
    });
  } else if (baseline?.month !== currentMonthKey) {
    const prevMonthAR = cumulativeAR - (baseline?.ar ?? 0);
    const prevMonthQR = cumulativeQR - (baseline?.qr ?? 0);
    const prevMonthPopular = cumulativePopular - (baseline?.popular ?? 0);

    writeJSON(PREV_MONTH_KEY, {
      month: baseline?.month,
      ar: Math.max(0, prevMonthAR),
      qr: Math.max(0, prevMonthQR),
      popular: Math.max(0, prevMonthPopular),
    });

    writeJSON(BASELINE_KEY, {
      month: currentMonthKey,
      ar: cumulativeAR,
      qr: cumulativeQR,
      popular: cumulativePopular,
    });
  }

  const activeBaseline = readJSON(BASELINE_KEY);
  const baseAR = activeBaseline?.ar ?? cumulativeAR;
  const baseQR = activeBaseline?.qr ?? cumulativeQR;
  const basePopular = activeBaseline?.popular ?? cumulativePopular;

  const totalAR = Math.max(0, cumulativeAR - baseAR);
  const totalQR = Math.max(0, cumulativeQR - baseQR);
  const popular = Math.max(0, cumulativePopular - basePopular);

  const arChange = prevMonth ? calcPercent(totalAR, prevMonth?.ar) : null;
  const qrChange = prevMonth ? calcPercent(totalQR, prevMonth?.qr) : null;
  const popularChange = prevMonth ? calcPercent(popular, prevMonth?.popular) : null;

  return { totalAR, totalQR, popular, arChange, qrChange, popularChange };
};

/**
 * Fetch monthly stats history for QR scans and AR engagements (up to 12 months).
 * Reads from dish_stats_monthly; current month falls back to the local baseline logic.
 */
export const fetchMonthlyStats = async (restaurantId, monthsCount = 12) => {
  requireRestaurantId(restaurantId);
  const monthLabels = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
  const now = new Date();
  const months = [];
  for (let i = monthsCount - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months?.push({
      key: `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, '0')}-01`,
      label: `${monthLabels?.[d?.getMonth()]} ${String(d?.getFullYear())?.slice(2)}`,
    });
  }

  const { data, error } = await supabase
    ?.from('dish_stats_monthly')
    ?.select('dish_id, month, ar_uses, qr_scans')
    ?.eq('restaurant_id', restaurantId)
    ?.gte('month', months?.[0]?.key);
  if (error) throw error;

  const totals = {};
  (data || [])?.forEach((row) => {
    const key = row?.month;
    if (!totals?.[key]) totals[key] = { qr: 0, ar: 0 };
    if (row?.dish_id === 'restaurant') {
      totals[key].qr += row?.qr_scans || 0;
    } else {
      totals[key].ar += row?.ar_uses || 0;
    }
  });

  return months?.map((m) => ({
    month: m?.key,
    label: m?.label,
    qr: totals?.[m?.key]?.qr || 0,
    ar: totals?.[m?.key]?.ar || 0,
  }));
};

/**
 * Fetch monthly views history per dish (last N months).
 * Returns { months: [{ key, label }], dishes: [{ dishId, dishName, total, values: { [monthKey]: number } }] }
 * "views" = AR views (ar_uses) per dish.
 */
export const fetchDishMonthlyHistory = async (restaurantId, monthsCount = 12) => {
  requireRestaurantId(restaurantId);
  const labels = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
  const now = new Date();
  const months = [];
  for (let i = monthsCount - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, '0')}-01`;
    months?.push({ key, label: `${labels?.[d?.getMonth()]} ${String(d?.getFullYear())?.slice(2)}` });
  }

  const { data, error } = await supabase
    ?.from('dish_stats_monthly')
    ?.select('dish_id, dish_name, month, ar_uses')
    ?.eq('restaurant_id', restaurantId)
    ?.gte('month', months?.[0]?.key)
    ?.order('month', { ascending: true });
  if (error) throw error;

  const map = new Map();
  (data || [])?.forEach((row) => {
    if (row?.dish_id === 'restaurant') return;
    if (!map?.has(row?.dish_id)) {
      map?.set(row?.dish_id, { dishId: row?.dish_id, dishName: row?.dish_name, total: 0, values: {} });
    }
    const entry = map?.get(row?.dish_id);
    entry.dishName = row?.dish_name || entry?.dishName;
    entry.values[row.month] = (entry?.values?.[row?.month] || 0) + (row?.ar_uses || 0);
    entry.total += row?.ar_uses || 0;
  });

  const dishes = Array.from(map?.values())?.sort((a, b) => b?.total - a?.total);
  return { months, dishes };
};

/**
 * Subscribe to real-time changes on dish_stats table
 */
export const subscribeToDishStats = (callback, restaurantId) => {
  return subscribeToRestaurantChanges({
    channelName: 'dish_stats_changes',
    table: 'dish_stats',
    restaurantId,
    callback,
  });
};
